
const MANAGED_BY = 'seo'

function ensureHead() {
  return document && document.head
}

function setTitle(title) {
  if (!title) return
  document.title = title
}

function createOrUpdateMeta({ name, property, content, key }) {
  if (!content && content !== 0) {
    // при пустом контенте — удаляем существующий тег
    const sel = name ? `meta[name="${name}"][data-managed-by="${MANAGED_BY}"]` :
                        `meta[property="${property}"][data-managed-by="${MANAGED_BY}"]`
    const exists = document.head.querySelector(sel)
    if (exists) exists.remove()
    return
  }

  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`
  // Попробуем найти уже существующий (приоритет: managed элемент, иначе любой существующий)
  let el = document.head.querySelector(`${selector}[data-managed-by="${MANAGED_BY}"]`) ||
           document.head.querySelector(selector)

  if (!el) {
    el = document.createElement('meta')
    if (name) el.setAttribute('name', name)
    if (property) el.setAttribute('property', property)
    el.setAttribute('data-managed-by', MANAGED_BY)
    if (key) el.setAttribute('data-seo-key', key)
    document.head.appendChild(el)
  } else {
    // пометить как управляемый, если мы переиспользуем существующий
    el.setAttribute('data-managed-by', MANAGED_BY)
    if (key) el.setAttribute('data-seo-key', key)
  }
  el.setAttribute('content', content)
}

function setCanonical(url) {
  if (!url) {
    const prev = document.head.querySelector('link[rel="canonical"][data-managed-by="' + MANAGED_BY + '"]')
    if (prev) prev.remove()
    return
  }
  let link = document.head.querySelector('link[rel="canonical"][data-managed-by="' + MANAGED_BY + '"]') ||
             document.head.querySelector('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    link.setAttribute('data-managed-by', MANAGED_BY)
    document.head.appendChild(link)
  } else {
    link.setAttribute('data-managed-by', MANAGED_BY)
  }
  link.setAttribute('href', url)
}

function setJsonLd(jsonLdObj) {
  // удаляем предыдущий
  const prev = document.head.querySelector('script[type="application/ld+json"][data-managed-by="' + MANAGED_BY + '"]')
  if (prev) prev.remove()

  if (!jsonLdObj) return

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-managed-by', MANAGED_BY)
  script.textContent = JSON.stringify(jsonLdObj)
  document.head.appendChild(script)
}

function setHreflangs(hreflangArr = []) {
  // удалить все созданные ранее
  const prev = Array.from(document.head.querySelectorAll('link[rel="alternate"][data-managed-by="' + MANAGED_BY + '"]'))
  prev.forEach(el => el.remove())

  if (!Array.isArray(hreflangArr) || hreflangArr.length === 0) return

  hreflangArr.forEach(h => {
    if (!h || !h.lang || !h.url) return
    const link = document.createElement('link')
    link.setAttribute('rel', 'alternate')
    link.setAttribute('hreflang', h.lang)
    link.setAttribute('href', h.url)
    link.setAttribute('data-managed-by', MANAGED_BY)
    document.head.appendChild(link)
  })
}

function sanitizeString(s) {
  if (!s && s !== '') return ''
  // простая защита: удалить теги
  return String(s).replace(/<\/?[^>]+(>|$)/g, '').trim()
}

/**
 * Главная функция: применяет seoSettings к head.
 * seoSettings — объект из store: landing_settings.seo
 */
export function applySeoSettings(seoSettings = {}) {
  if (typeof window === 'undefined') return
  const head = ensureHead()
  if (!head) return

  // title
  const title = sanitizeString(seoSettings.meta_title || '')
  if (title) setTitle(title)

  // description
  const description = sanitizeString(seoSettings.meta_description || '')
  createOrUpdateMeta({ name: 'description', content: description, key: 'description' })

  // keywords (если надо)
  if (Array.isArray(seoSettings.meta_keywords) && seoSettings.meta_keywords.length) {
    createOrUpdateMeta({ name: 'keywords', content: seoSettings.meta_keywords.join(', '), key: 'keywords' })
  } else {
    // удалить если пусто
    const m = document.head.querySelector('meta[name="keywords"][data-managed-by="' + MANAGED_BY + '"]')
    if (m) m.remove()
  }

  // robots (учесть флаг noindex_until_published)
  let robots = seoSettings.robots || ''
  if (seoSettings.noindex_until_published) {
    robots = 'noindex, nofollow'
  }
  if (robots) createOrUpdateMeta({ name: 'robots', content: robots, key: 'robots' })
  else {
    const r = document.head.querySelector('meta[name="robots"][data-managed-by="' + MANAGED_BY + '"]')
    if (r) r.remove()
  }

  // canonical
  setCanonical(seoSettings.canonical || '')

  // Open Graph (og:title, og:description, og:image, og:type)
  const og = seoSettings.open_graph || {}
  createOrUpdateMeta({ property: 'og:title', content: sanitizeString(og['og:title'] || seoSettings.meta_title || ''), key: 'og:title' })
  createOrUpdateMeta({ property: 'og:description', content: sanitizeString(og['og:description'] || seoSettings.meta_description || ''), key: 'og:description' })
  if (og['og:image']) {
    createOrUpdateMeta({ property: 'og:image', content: og['og:image'], key: 'og:image' })
  } else {
    const el = document.head.querySelector('meta[property="og:image"][data-managed-by="' + MANAGED_BY + '"]')
    if (el) el.remove()
  }
  if (og['og:type']) createOrUpdateMeta({ property: 'og:type', content: og['og:type'], key: 'og:type' })

  // Twitter
  const tw = seoSettings.twitter || {}
  if (tw.card) createOrUpdateMeta({ name: 'twitter:card', content: tw.card, key: 'twitter:card' })
  if (tw.site) createOrUpdateMeta({ name: 'twitter:site', content: tw.site, key: 'twitter:site' })
  if (tw.title) createOrUpdateMeta({ name: 'twitter:title', content: sanitizeString(tw.title), key: 'twitter:title' })
  if (tw.image) createOrUpdateMeta({ name: 'twitter:image', content: tw.image, key: 'twitter:image' })

  // JSON-LD
  if (seoSettings.structured_data && seoSettings.structured_data.enabled) {
    setJsonLd(seoSettings.structured_data.json_ld || {})
  } else {
    setJsonLd(null)
  }

  // hreflang
  setHreflangs(seoSettings.hreflang || [])

  // meta robots directives (noarchive, nosnippet, noimageindex)
  const mrd = seoSettings.meta_robots_directives || {}
  const directives = []
  if (mrd.noarchive) directives.push('noarchive')
  if (mrd.nosnippet) directives.push('nosnippet')
  if (mrd.noimageindex) directives.push('noimageindex')
  if (directives.length) createOrUpdateMeta({ name: 'googlebot', content: directives.join(','), key: 'meta_robots_directives' })
  else {
    const el = document.head.querySelector('meta[name="googlebot"][data-managed-by="' + MANAGED_BY + '"]')
    if (el) el.remove()
  }

  // updated_at — можно положить в meta для кэш-инвалидации (по желанию)
  if (seoSettings.updated_at) {
    createOrUpdateMeta({ name: 'seo-updated-at', content: seoSettings.updated_at, key: 'seo-updated-at' })
  }
}
